import React, { useState, useRef, useEffect } from 'react';
import { 
  Navbar, Nav, Container, Row, Col, Card, Button, 
  Form, Alert, Badge
} from 'react-bootstrap';
import emailjs from '@emailjs/browser';
import { 
  Building2, MapPin, Mail, Phone, 
  Facebook, Linkedin, Instagram, 
  Home, Users, ChevronRight,
  Shield, Cpu, BarChart, CheckCircle,
  Clock, Headphones, ShieldCheck,
  TrendingUp, Award, PenTool,
  Image as ImageIcon,
  Sun, Moon, Search, X,
  MessageCircle, Video, Cloud, Youtube,
  Zap, Target, Briefcase, Settings, Code, Globe,
  ArrowRight, BookOpen, Layers, Settings2, Database, Workflow, GraduationCap
} from 'lucide-react';

import Carousel from 'react-bootstrap/Carousel';
import { PopupButton } from 'react-calendly';

// --- FIREBASE INTEGRATION ---
import { db } from './firebase'; 
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

// --- IMAGE UPLOAD COMPONENT ---
import ImageUpload from './ImageUpload';

// --- MAP COMPONENT (Leaflet) ---
import { MapContainer, TileLayer, Marker, Popup as MapPopup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import 'bootstrap/dist/css/bootstrap.min.css';
import "./App.css";
/* global Tawk_API */

// Fix Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Modern UI/UX CSS with Premium Corporate Theme - Navy Blue and Deep Red Accent
const modernStyles = `
  :root {
    --primary-navy: #0F2D62;
    --primary-navy-light: #1a3d7a;
    --primary-navy-dark: #0a1f42;
    --accent-red: #C61F2C;
    --accent-red-light: #d43a46;
    --accent-red-dark: #a01824;
    --dark-bg: #0F172A;
    --dark-card: #1E293B;
    --light-bg: #FFFFFF;
    --light-card: #F8F9FB;
    --text-dark: #1B1B1B;
    --text-body: #555555;
    --text-muted: #888888;
    --border-light: #E8E8E8;
    --transition-smooth: all 0.3s ease;
    --shadow-soft: 0 4px 20px rgba(0,0,0,0.06);
    --shadow-hover: 0 12px 40px rgba(0,0,0,0.08);
  }

  body.dark-mode {
    background-color: var(--dark-bg);
    color: #F1F5F9;
  }

  body.dark-mode .card {
    background-color: var(--dark-card);
    border-color: #334155;
    color: #F1F5F9;
  }

  body.dark-mode .bg-light {
    background-color: var(--dark-card) !important;
  }

  body.dark-mode .text-muted {
    color: #94A3B8 !important;
  }

  body.dark-mode .navbar {
    background-color: var(--dark-card) !important;
    border-bottom-color: #334155 !important;
  }

  body.dark-mode .navbar .text-dark {
    color: #F1F5F9 !important;
  }

  .theme-toggle-btn {
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 50%;
    transition: var(--transition-smooth);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--accent-red);
  }

  .theme-toggle-btn:hover {
    transform: rotate(15deg);
    background: rgba(198, 31, 44, 0.08);
  }

  .modern-card {
    transition: var(--transition-smooth);
    border: none;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: var(--shadow-soft);
    background: var(--light-bg);
  }

  .modern-card:hover {
    transform: translateY(-6px);
    box-shadow: var(--shadow-hover);
  }

  .property-popup-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0,0,0,0.7);
    backdrop-filter: blur(4px);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: fadeIn 0.3s ease;
  }

  .property-popup-content {
    width: 90%;
    max-width: 1200px;
    height: 80vh;
    background: white;
    border-radius: 20px;
    overflow: hidden;
    position: relative;
    animation: slideUp 0.4s ease;
    box-shadow: 0 20px 60px rgba(0,0,0,0.15);
  }

  body.dark-mode .property-popup-content {
    background: var(--dark-card);
  }

  .popup-close-btn {
    position: absolute;
    top: 20px;
    right: 20px;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: var(--accent-red);
    border: none;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 10;
    transition: var(--transition-smooth);
  }

  .popup-close-btn:hover {
    transform: rotate(90deg);
    background: var(--accent-red-dark);
  }

  .popup-grid {
    display: grid;
    grid-template-columns: 1.5fr 1fr;
    height: 100%;
  }

  .popup-left {
    padding: 30px;
    overflow-y: auto;
  }

  .popup-right {
    background: linear-gradient(135deg, var(--primary-navy), var(--primary-navy-light));
    padding: 30px;
    color: white;
    overflow-y: auto;
  }

  body.dark-mode .popup-right {
    background: linear-gradient(135deg, #2d2d2d, #1a1a1a);
  }

  .map-container {
    height: 300px;
    border-radius: 16px;
    overflow: hidden;
    margin: 20px 0;
    box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  }

  .video-container {
    height: 300px;
    border-radius: 16px;
    overflow: hidden;
    margin: 20px 0;
    box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  }

  .video-container iframe {
    width: 100%;
    height: 100%;
    border: none;
  }

  .review-card {
    background: rgba(255,255,255,0.05);
    backdrop-filter: blur(10px);
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 15px;
    border: 1px solid rgba(255,255,255,0.08);
  }

  body.dark-mode .review-card {
    background: rgba(0,0,0,0.2);
  }

  .star-rating {
    color: #FFD700;
    font-size: 1.2rem;
    margin-bottom: 10px;
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes slideUp {
    from { transform: translateY(30px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }

  .filter-bar {
    background: white;
    border-radius: 12px;
    padding: 12px 24px;
    box-shadow: var(--shadow-soft);
    display: flex;
    gap: 15px;
    flex-wrap: wrap;
    align-items: center;
    margin-bottom: 30px;
    border: 1px solid var(--border-light);
  }

  body.dark-mode .filter-bar {
    background: var(--dark-card);
    border: 1px solid #334155;
  }

  .filter-input {
    flex: 1;
    min-width: 250px;
    border: 1px solid var(--border-light);
    border-radius: 8px;
    padding: 10px 18px;
    font-size: 0.95rem;
    transition: var(--transition-smooth);
    background: var(--light-bg);
    color: var(--text-dark);
  }

  body.dark-mode .filter-input {
    background: var(--dark-bg);
    border-color: #334155;
    color: white;
  }

  .filter-input:focus {
    outline: none;
    border-color: var(--primary-navy);
    box-shadow: 0 0 0 3px rgba(15, 45, 98, 0.1);
  }

  .filter-select {
    border: 1px solid var(--border-light);
    border-radius: 8px;
    padding: 10px 18px;
    font-size: 0.95rem;
    cursor: pointer;
    transition: var(--transition-smooth);
    background: var(--light-bg);
    color: var(--text-dark);
  }

  body.dark-mode .filter-select {
    background: var(--dark-bg);
    border-color: #334155;
    color: white;
  }

  .toggle-switch {
    display: flex;
    background: var(--light-card);
    border-radius: 8px;
    padding: 4px;
    border: 1px solid var(--border-light);
  }

  body.dark-mode .toggle-switch {
    background: var(--dark-bg);
  }

  .toggle-option {
    padding: 8px 24px;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 500;
    transition: var(--transition-smooth);
    font-size: 0.9rem;
  }

  .toggle-option.active {
    background: var(--primary-navy);
    color: white;
  }

  .service-preview-card {
    background: var(--light-bg);
    border-radius: 16px;
    overflow: hidden;
    box-shadow: var(--shadow-soft);
    transition: var(--transition-smooth);
    height: 100%;
    display: flex;
    flex-direction: column;
    border: 1px solid var(--border-light);
  }

  body.dark-mode .service-preview-card {
    background: var(--dark-card);
  }

  .service-preview-card:hover {
    transform: translateY(-6px);
    box-shadow: var(--shadow-hover);
  }

  .service-preview-image {
    height: 220px;
    overflow: hidden;
    position: relative;
  }

  .service-preview-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: var(--transition-smooth);
  }

  .service-preview-card:hover .service-preview-image img {
    transform: scale(1.03);
  }

  .service-preview-content {
    padding: 24px;
    flex: 1;
  }

  .service-preview-content h3 {
    font-size: 1.3rem;
    font-weight: 700;
    margin-bottom: 10px;
    color: var(--primary-navy);
  }

  .service-preview-content p {
    color: var(--text-body);
    line-height: 1.7;
    margin-bottom: 16px;
    font-size: 0.95rem;
  }

  body.dark-mode .service-preview-content p {
    color: #94A3B8;
  }

  .view-more-btn {
    background: transparent;
    border: 1px solid var(--accent-red);
    color: var(--accent-red);
    padding: 10px 24px;
    border-radius: 8px;
    font-weight: 600;
    transition: var(--transition-smooth);
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font-size: 0.9rem;
  }

  .view-more-btn:hover {
    background: var(--accent-red);
    color: white;
    transform: translateX(4px);
  }

  @media (max-width: 992px) {
    .popup-grid {
      grid-template-columns: 1fr;
    }
    
    .popup-right {
      display: none;
    }
    
    .property-popup-content {
      width: 95%;
      height: 90vh;
    }
  }

  @media (max-width: 768px) {
    .filter-bar {
      flex-direction: column;
      border-radius: 12px;
      padding: 15px;
    }
    
    .filter-input, .filter-select {
      width: 100%;
      min-width: auto;
    }
    
    .property-tab {
      width: 100%;
      margin: 5px 0 !important;
    }
    
    .display-4 {
      font-size: 2rem;
    }
    
    .display-5 {
      font-size: 1.8rem;
    }
    
    .service-preview-card {
      margin-bottom: 20px;
    }
    
    .popup-left {
      padding: 15px;
    }
    
    .popup-content {
      width: 95%;
      height: 95vh;
    }
    
    .video-container {
      height: 250px;
    }
    
    .map-container {
      height: 200px;
    }
    
    .popup-grid {
      grid-template-columns: 1fr;
    }
    
    .popup-right {
      display: none;
    }
    
    .whatsapp-float {
      width: 50px;
      height: 50px;
      bottom: 15px;
      right: 15px;
    }
    
    .whatsapp-float svg {
      width: 25px;
      height: 25px;
    }
    
    .whatsapp-tooltip {
      right: 75px;
      font-size: 12px;
      padding: 5px 10px;
    }
    
    .footer-schedule-btn {
      width: 100%;
      margin-top: 10px;
    }
    
    .modern-card {
      margin-bottom: 20px;
    }
    
    .service-preview-image {
      height: 200px;
    }
    
    .service-preview-content h3 {
      font-size: 1.2rem;
    }
    
    .service-preview-content p {
      font-size: 0.9rem;
    }
    
    .view-more-btn {
      padding: 8px 18px;
      font-size: 0.85rem;
    }

    .hero-container {
      min-height: 400px !important;
    }

    .hero-title {
      font-size: 1.8rem !important;
    }

    .hero-subtitle {
      font-size: 2.2rem !important;
    }

    .hero-text {
      font-size: 1rem !important;
    }

    .hero-buttons {
      flex-direction: column;
      gap: 10px !important;
    }

    .hero-buttons button,
    .hero-buttons .popup-button {
      width: 100%;
      margin: 0 !important;
    }
  }
  
  @media (max-width: 480px) {
    .display-4 {
      font-size: 1.8rem;
    }
    
    .display-5 {
      font-size: 1.5rem;
    }
    
    .lead {
      font-size: 1rem;
    }
    
    .navbar-brand img {
      height: 50px;
    }
    
    .theme-toggle-btn {
      font-size: 1.2rem;
    }
    
    .popup-close-btn {
      width: 35px;
      height: 35px;
      top: 10px;
      right: 10px;
    }
    
    .video-container {
      height: 200px;
    }
    
    .map-container {
      height: 150px;
    }

    .hero-title {
      font-size: 1.5rem !important;
    }

    .hero-subtitle {
      font-size: 1.8rem !important;
    }

    .container {
      padding-left: 15px;
      padding-right: 15px;
    }
  }

  @media (max-width: 991px) {
    .navbar-collapse {
      background-color: inherit;
      padding: 20px;
      border-radius: 12px;
      margin-top: 10px;
      box-shadow: var(--shadow-soft);
      border: 1px solid var(--border-light);
    }
    
    body.dark-mode .navbar-collapse {
      background-color: var(--dark-card);
    }
    
    .navbar-nav {
      gap: 10px !important;
    }
    
    .navbar-nav .nav-link {
      width: 100%;
      text-align: center;
    }
    
    .theme-toggle-btn {
      margin: 10px auto 0 !important;
    }
  }
`;

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error: error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '100vh',
          flexDirection: 'column',
          padding: '20px',
          textAlign: 'center'
        }}>
          <h2 style={{ color: '#C61F2C', marginBottom: '10px' }}>Something went wrong</h2>
          <p style={{ color: '#666', maxWidth: '500px' }}>Please refresh the page or try again later.</p>
          <button 
            onClick={() => window.location.reload()}
            style={{
              marginTop: '20px',
              padding: '10px 30px',
              backgroundColor: '#C61F2C',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            Refresh Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Initializing EmailJS
emailjs.init("LL4BkHpRLkDCAZsvj");

const SocialIcon = ({ href, icon, accentColor, isMobile }) => (
  <a 
    href={href} 
    target="_blank" 
    rel="noopener noreferrer"
    className="d-flex align-items-center justify-content-center rounded-circle"
    style={{ 
      width: isMobile ? '34px' : '40px', 
      height: isMobile ? '34px' : '40px', 
      backgroundColor: 'rgba(198, 31, 44, 0.08)',
      color: '#C61F2C',
      transition: 'all 0.3s ease',
      textDecoration: 'none'
    }}
    onMouseEnter={(e) => {
      e.target.style.backgroundColor = '#C61F2C';
      e.target.style.color = 'white';
      e.target.style.transform = 'translateY(-3px)';
    }}
    onMouseLeave={(e) => {
      e.target.style.backgroundColor = 'rgba(198, 31, 44, 0.08)';
      e.target.style.color = '#C61F2C';
      e.target.style.transform = 'translateY(0)';
    }}
  >
    {icon}
  </a>
);

const App = () => {
  const form = useRef();
  const [status, setStatus] = useState({ type: '', msg: '' });
  const accentColor = "#C61F2C";
  const primaryColor = "#0F2D62";
  const [searchQuery, setSearchQuery] = useState('');
  
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [activePropertyTab, setActivePropertyTab] = useState('tenant');
  const [propertyType, setPropertyType] = useState('sale');
  const [filters, setFilters] = useState({
    minPrice: '',
    maxPrice: '',
    bedrooms: '',
    propertyType: 'all'
  });
  const [selectedPropertyForPopup, setSelectedPropertyForPopup] = useState(null);
  const [showPropertyPopup, setShowPropertyPopup] = useState(false);
  const [reviews, setReviews] = useState({});
  const [newReview, setNewReview] = useState({ rating: 5, comment: '' });
  const [darkMode, setDarkMode] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // useEffect(() => {
  //   const savedMode = localStorage.getItem('darkMode');
  //   if (savedMode !== null) {
  //     setDarkMode(savedMode === 'true');
  //     if (savedMode === 'true') {
  //       document.body.classList.add('dark-mode');
  //     }
  //   } else {
  //     setDarkMode(true);
  //     document.body.classList.add('dark-mode');
  //     localStorage.setItem('darkMode', 'true');
  //   }
  // }, []);

  useEffect(() => {
  // Always use light mode
  setDarkMode(false);
  document.body.classList.remove('dark-mode');
  localStorage.setItem('darkMode', 'false');
}, []);

 

  const sendEmail = async (e) => {
    e.preventDefault();
    setStatus({ type: 'info', msg: 'Sending message...' });

    try {
      const result = await emailjs.sendForm(
        'service_hdv7h1i',
        'template_c0qfw3i',
        form.current,
        { publicKey: 'LL4BkHpRLkDCAZsvj' }
      );
      
      console.log('SUCCESS!', result);
      
      try {
        const formData = new FormData(form.current);
        await addDoc(collection(db, "inquiries"), {
          name: formData.get('from_name'),
          email: formData.get('reply_to'),
          phone: formData.get('phone_number'),
          service_interest: formData.get('service_interest'),
          message: formData.get('message'),
          timestamp: serverTimestamp(),
          status: 'new'
        });
      } catch (firebaseError) {
        console.log('Firebase error but email sent:', firebaseError);
      }
      
      setStatus({ type: 'success', msg: '✅ Message sent successfully! We\'ll contact you within 24 hours.' });
      form.current.reset();
      
    } catch (error) {
      console.error("EmailJS Error:", error);
      setStatus({ type: 'danger', msg: '❌ Error: Failed to send. Please try again.' });
    }
  };

  const handleAddImage = (propertyId, propertyTitle) => {
    setSelectedProperty({
      id: propertyId,
      title: propertyTitle
    });
    setShowImageUpload(true);
  };

  const openPropertyPopup = (property) => {
    setSelectedPropertyForPopup(property);
    setShowPropertyPopup(true);
    document.body.style.overflow = 'hidden';
  };

  const closePropertyPopup = () => {
    setShowPropertyPopup(false);
    setSelectedPropertyForPopup(null);
    document.body.style.overflow = 'auto';
  };

  const addReview = (propertyId) => {
    if (!newReview.comment.trim()) return;
    
    const review = {
      id: Date.now(),
      user: "Guest User",
      rating: newReview.rating,
      comment: newReview.comment,
      date: new Date().toISOString().split('T')[0]
    };
    
    setReviews(prev => ({
      ...prev,
      [propertyId]: [...(prev[propertyId] || []), review]
    }));
    
    setNewReview({ rating: 5, comment: '' });
  };

  const renderStars = (rating, interactive = false, onChange = null) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span
          key={i}
          style={{ 
            cursor: interactive ? 'pointer' : 'default',
            color: i <= rating ? '#C61F2C' : '#e0e0e0',
            fontSize: interactive ? '2rem' : '1.2rem',
            transition: 'all 0.2s ease'
          }}
          onClick={() => interactive && onChange && onChange(i)}
          onMouseEnter={(e) => interactive && (e.target.style.transform = 'scale(1.2)')}
          onMouseLeave={(e) => interactive && (e.target.style.transform = 'scale(1)')}
        >
          ★
        </span>
      );
    }
    return stars;
  };

  const isMobile = windowWidth <= 768;

  // Smooth scroll to section
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Navigation click handler - scroll to section on homepage
  const handleNavClick = (sectionId) => {
    setExpanded(false);
    setTimeout(() => {
      scrollToSection(sectionId);
    }, 300);
  };

  // Wrapped PopupButton component to fix the "Functions are not valid as a React child" error
  const CalendlyPopupButton = ({ url, text, className, style, onMouseEnter, onMouseLeave }) => {
    return (
      <PopupButton
        url={url}
        rootElement={document.getElementById("root")}
        text={text}
        className={className}
        style={style}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      />
    );
  };

  return (
    <ErrorBoundary>
      <div style={{ 
        fontFamily: "'Inter', 'Poppins', sans-serif", 
        backgroundColor: darkMode ? '#0F172A' : '#FFFFFF', 
        minHeight: '100vh', 
        transition: 'all 0.3s ease', 
        paddingTop: isMobile ? '70px' : '80px' 
      }}>
        
        <style>{modernStyles}</style>

        {/* Navigation Bar - Clean Grey with Icon Images */}
        <Navbar 
          bg={darkMode ? 'dark' : 'white'} 
          variant={darkMode ? 'dark' : 'light'} 
          expand="lg" 
          fixed="top" 
          expanded={expanded}
          onToggle={() => setExpanded(!expanded)}
          style={{ 
            backgroundColor: darkMode ? '#1E293B' : '#E8E8E8 !important',
            borderBottom: `1px solid ${darkMode ? '#334155' : '#D0D0D0'}`,
            padding: '6px 0'
          }}
        >
          <Container>
            <Navbar.Brand style={{ cursor: 'pointer' }}>
              <img 
                src="/logo5.png" 
                alt="logo" 
                className="custom-logo"
              />
            </Navbar.Brand>
            
            <Navbar.Toggle aria-controls="basic-navbar-nav" />
            <Navbar.Collapse id="basic-navbar-nav">
              <Nav className="ms-auto align-items-center" style={{ gap: isMobile ? '5px' : '20px' }}>
                
                {/* HOME */}
                <Nav.Link 
                  onClick={() => handleNavClick('home')} 
                  style={{ 
                    cursor: 'pointer',
                    padding: isMobile ? '6px 12px' : '6px 8px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '2px'
                  }}
                >
                  <img 
                    src="/navhome3.png" 
                    alt="Home" 
                    style={{ 
                      height: isMobile ? '24px' : '28px', 
                      width: isMobile ? '24px' : '28px',
                      opacity: 1
                    }} 
                  />
                  <span style={{ 
                    color: '#555555',
                    fontWeight: '400',
                    fontSize: '0.7rem',
                    letterSpacing: '0.3px'
                  }}>
                    HOME
                  </span>
                </Nav.Link>

                {/* ABOUT */}
                <Nav.Link 
                  onClick={() => handleNavClick('about')} 
                  style={{ 
                    cursor: 'pointer',
                    padding: isMobile ? '6px 12px' : '6px 8px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '2px'
                  }}
                >
                  <img 
                    src="/navabout3.png" 
                    alt="About" 
                    style={{ 
                      height: isMobile ? '24px' : '28px', 
                      width: isMobile ? '24px' : '28px',
                      opacity: 1
                    }} 
                  />
                  <span style={{ 
                    color: '#555555',
                    fontWeight: '400',
                    fontSize: '0.7rem',
                    letterSpacing: '0.3px'
                  }}>
                    ABOUT
                  </span>
                </Nav.Link>

                {/* SERVICES */}
                <Nav.Link 
                  onClick={() => handleNavClick('services')} 
                  style={{ 
                    cursor: 'pointer',
                    padding: isMobile ? '6px 12px' : '6px 8px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '2px'
                  }}
                >
                  <img 
                    src="/navservice3.png" 
                    alt="Services" 
                    style={{ 
                      height: isMobile ? '24px' : '28px', 
                      width: isMobile ? '24px' : '28px',
                      opacity: 1
                    }} 
                  />
                  <span style={{ 
                    color: '#555555',
                    fontWeight: '400',
                    fontSize: '0.7rem',
                    letterSpacing: '0.3px'
                  }}>
                    SERVICES
                  </span>
                </Nav.Link>

                {/* CONTACT */}
                <Nav.Link 
                  onClick={() => handleNavClick('contact')} 
                  style={{ 
                    cursor: 'pointer',
                    padding: isMobile ? '6px 12px' : '6px 8px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '2px'
                  }}
                >
                  <img 
                    src="/navcontact3.png" 
                    alt="Contact" 
                    style={{ 
                      height: isMobile ? '24px' : '28px', 
                      width: isMobile ? '24px' : '28px',
                      opacity: 1
                    }} 
                  />
                  <span style={{ 
                    color: '#555555',
                    fontWeight: '400',
                    fontSize: '0.7rem',
                    letterSpacing: '0.3px'
                  }}>
                    CONTACT
                  </span>
                </Nav.Link>
              </Nav>
            </Navbar.Collapse>
          </Container>
        </Navbar>
             
       {/* ===== HOME PAGE (Landing Page) ===== */}
<>
  {/* Hero Section - Conditional Rendering for Desktop vs Mobile */}
  <header id="home" className="position-relative" style={{ 
    marginTop: '0',
    padding: '0',
    marginBottom: '0',
    position: 'relative',
    width: '100%'
  }}>
    {/* ===== DESKTOP HERO - EXACTLY AS ORIGINAL ===== */}
    {!isMobile ? (
      <div style={{
        position: 'relative',
        width: '100%',
        minHeight: '100vh',
        height: '100vh',
        overflow: 'hidden',
        margin: '0 auto'
      }}>
        {/* Background Image - Full screen */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url('/coverbg.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          zIndex: 1
        }} />
        
        {/* Content - Subtle dark gradient for text readability */}
        <div style={{
          position: 'relative',
          zIndex: 2,
          height: '100%',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          padding: '0 60px',
          background: 'linear-gradient(to right, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.1) 100%)'
        }}>
          <div style={{
            maxWidth: '700px',
            width: '100%'
          }}>
            <div style={{
              display: 'inline-block',
              backgroundColor: 'rgba(255,255,255,0.15)',
              padding: '4px 14px',
              borderRadius: '20px',
              marginBottom: '14px',
              border: '1px solid rgba(255,255,255,0.2)'
            }}>
              <span style={{
                fontSize: '0.7rem',
                fontWeight: '500',
                color: 'rgba(255,255,255,0.85)',
                letterSpacing: '1px',
                textTransform: 'uppercase'
              }}>
                Product Lifecycle Management
              </span>
            </div>
            
            <h1 style={{
              fontSize: '2.8rem',
              fontWeight: '700',
              lineHeight: '1.2',
              color: '#FFFFFF',
              marginBottom: '2px',
              letterSpacing: '-0.01em',
              textShadow: '0 2px 20px rgba(0,0,0,0.4)'
            }}>
              Empowering Digital Engineering
            </h1>
            
            <h1 style={{
              fontSize: '2.8rem',
              fontWeight: '700',
              lineHeight: '1.2',
              color: '#FFFFFF',
              marginBottom: '14px',
              letterSpacing: '-0.01em',
              textShadow: '0 2px 20px rgba(0,0,0,0.4)'
            }}>
              with <span style={{ color: '#C61F2C' }}>PLM</span> Excellence
            </h1>
            
            <p style={{
              fontSize: '1.05rem',
              lineHeight: '1.6',
              maxWidth: '500px',
              color: 'rgba(255,255,255,0.85)',
              marginBottom: '24px',
              fontWeight: '400',
              textShadow: '0 1px 12px rgba(0,0,0,0.3)'
            }}>
              Helping organizations accelerate product innovation through Siemens Teamcenter, NX, and end-to-end Product Lifecycle Management solutions.
            </p>

            <div style={{
              display: 'flex',
              gap: '14px',
              marginBottom: '30px',
              flexDirection: 'row'
            }}>
              <Button
                onClick={() => scrollToSection('about')}
                style={{
                  width: 'auto',
                  padding: '14px 32px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  borderRadius: '8px',
                  backgroundColor: '#C61F2C',
                  color: 'white',
                  border: 'none',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#0F2D62';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 8px 24px rgba(15, 45, 98, 0.25)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#C61F2C';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                Know More →
              </Button>
              
              <CalendlyPopupButton
                url="https://calendly.com/sahilumate32/30min"
                text="📅 Schedule a Demo"
                className="calendly-outline-btn"
                style={{ 
                  width: 'auto',
                  textAlign: 'center',
                  backgroundColor: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  color: '#FFFFFF',
                  borderRadius: '8px',
                  fontWeight: '500',
                  padding: '14px 32px',
                  fontSize: '0.9rem',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  display: 'inline-block',
                  backdropFilter: 'blur(4px)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'rgba(255,255,255,0.2)';
                  e.target.style.borderColor = 'white';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'rgba(255,255,255,0.08)';
                  e.target.style.borderColor = 'rgba(255,255,255,0.3)';
                }}
              />
            </div>
            
            <div>
              <p style={{
                letterSpacing: '1px',
                fontSize: '0.7rem',
                color: 'rgba(255,255,255,0.5)',
                marginBottom: '3px',
                textTransform: 'uppercase',
                fontWeight: '400'
              }}>
                TRUSTED PARTNERS
              </p>
              <p style={{
                fontWeight: '500',
                fontSize: '1rem',
                color: 'rgba(255,255,255,0.8)'
              }}>
                Serving Ontario businesses since 2020
              </p>
            </div>
          </div>
        </div>
      </div>
    ) : (
      /* ===== MOBILE HERO - DEDICATED MOBILE LAYOUT ===== */
      <div style={{
        position: 'relative',
        width: '100%',
        height: '400px',
        overflow: 'hidden',
        backgroundColor: '#FFFFFF',
        display: 'flex',
        alignItems: 'center',
        padding: '16px 20px'
      }}>
        {/* Background Image - Right side, naturally cropped */}
        <div style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: '55%',
          backgroundImage: `url('/coverbg.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'right center',
          backgroundRepeat: 'no-repeat',
          zIndex: 1,
          opacity: 0.9
        }} />
        
        {/* Content Overlay - White gradient for text readability */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          width: '68%',
          background: 'linear-gradient(90deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.85) 60%, rgba(255,255,255,0) 100%)',
          zIndex: 2
        }} />
        
        {/* Content Container - WIDER text column */}
        <div style={{
          position: 'relative',
          zIndex: 3,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          height: '100%',
          width: '58%',
          maxWidth: '240px'
        }}>
          {/* Badge */}
          <div style={{
            display: 'inline-block',
            backgroundColor: '#F2F4F7',
            padding: '4px 10px',
            borderRadius: '14px',
            marginBottom: '6px',
            border: '1px solid #E5E7EB',
            alignSelf: 'flex-start'
          }}>
            <span style={{
              fontSize: '9px',
              fontWeight: '600',
              color: '#0F2D62',
              letterSpacing: '0.3px',
              textTransform: 'uppercase'
            }}>
              Product Lifecycle Management
            </span>
          </div>
          
          {/* Heading - Exactly 2 lines with wider container */}
          <h1 style={{
            fontSize: '22px',
            fontWeight: '700',
            lineHeight: '1.08',
            color: '#1A1A1A',
            marginBottom: '0px',
            letterSpacing: '-0.5px',
            maxWidth: '100%',
            whiteSpace: 'nowrap'
          }}>
            Empowering Digital Engineering
          </h1>
          
          {/* Sub Heading */}
          <h1 style={{
            fontSize: '22px',
            fontWeight: '700',
            lineHeight: '1.08',
            color: '#1A1A1A',
            marginBottom: '4px',
            letterSpacing: '-0.5px',
            maxWidth: '100%',
            whiteSpace: 'nowrap'
          }}>
            with <span style={{ color: '#C61F2C' }}>PLM</span> Excellence
          </h1>
          
          {/* Paragraph */}
          <p style={{
            fontSize: '12px',
            lineHeight: '1.45',
            maxWidth: '220px',
            color: '#444444',
            marginTop: '8px',
            marginBottom: '12px',
            fontWeight: '400'
          }}>
            Helping organizations accelerate product innovation through Siemens Teamcenter, NX, and end-to-end Product Lifecycle Management solutions.
          </p>

          {/* Buttons - Both exactly the same size */}
          <div style={{
            display: 'flex',
            gap: '6px',
            marginBottom: '10px',
            flexDirection: 'column',
            width: '100%'
          }}>
            {/* Know More Button */}
            <Button
              onClick={() => scrollToSection('about')}
              style={{
                width: '165px',
                height: '38px',
                padding: '8px 16px',
                fontSize: '12px',
                fontWeight: '600',
                borderRadius: '8px',
                backgroundColor: '#C61F2C',
                color: 'white',
                border: 'none',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxSizing: 'border-box',
                margin: 0
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#0F2D62';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 8px 24px rgba(15, 45, 98, 0.25)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#C61F2C';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              Know More →
            </Button>
            
            {/* Schedule a Demo Button - EXACT same size as Know More */}
            <CalendlyPopupButton
              url="https://calendly.com/sahilumate32/30min"
              text="📅 Schedule a Demo"
              className="calendly-outline-btn"
              style={{ 
                width: '165px',
                height: '38px',
                padding: '8px 16px',
                fontSize: '12px',
                fontWeight: '600',
                borderRadius: '8px',
                backgroundColor: 'transparent',
                border: '1px solid #C61F2C',
                color: '#C61F2C',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxSizing: 'border-box',
                margin: 0
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#C61F2C';
                e.target.style.color = 'white';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 8px 24px rgba(198, 31, 44, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#C61F2C';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
          
          {/* Trusted Partners */}
          <div>
            <p style={{
              letterSpacing: '0.3px',
              fontSize: '9px',
              color: '#888888',
              marginBottom: '0px',
              textTransform: 'uppercase',
              fontWeight: '500'
            }}>
              TRUSTED PARTNERS
            </p>
            <p style={{
              fontWeight: '500',
              fontSize: '10px',
              color: '#555555'
            }}>
              Serving Ontario businesses since 2020
            </p>
          </div>
        </div>
      </div>
    )}
  </header>

         {/* ===== ABOUT SECTION ===== */}
  <section id="about" className="py-5" style={{ 
    backgroundColor: '#FFFFFF'
  }}>
    <Container>
      <Row className="justify-content-center mb-4">
        <Col lg={10} className="text-center">
          <div className="d-inline-block mb-3">
            <div className="d-flex align-items-center gap-2">
              <div style={{ width: '40px', height: '2px', backgroundColor: '#C61F2C' }}></div>
              <span className="text-uppercase fw-bold" style={{ color: '#C61F2C', letterSpacing: '2px', fontSize: isMobile ? '0.7rem' : '0.8rem' }}>About Us</span>
              <div style={{ width: '40px', height: '2px', backgroundColor: '#C61F2C' }}></div>
            </div>
          </div>
          <h2 style={{ 
            color: '#1B1B1B', 
            fontSize: isMobile ? '2rem' : '2.8rem', 
            fontWeight: '700',
            marginBottom: '12px'
          }}>
            Who <span style={{ color: '#C61F2C' }}>We Are</span>
          </h2>
          <p style={{ 
            color: '#555555', 
            fontSize: isMobile ? '1rem' : '1.15rem',
            maxWidth: '700px',
            margin: '0 auto',
            lineHeight: '1.7'
          }}>
            A1 Marketsense is a premier PLM and IT consulting firm serving businesses across Ontario since 2020.
          </p>
        </Col>
      </Row>

      <Row className="g-5 align-items-center mt-3">
        <Col lg={6}>
          <div className="position-relative">
            <div className="rounded-4 overflow-hidden" style={{ 
              height: isMobile ? '280px' : '380px',
              backgroundColor: '#F8F9FB',
              border: '1px solid #E8E8E8',
              borderRadius: '16px',
              boxShadow: '0 4px 30px rgba(0,0,0,0.04)'
            }}>
              <img src="/about5.png" alt="About Us" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
            <div style={{
              position: 'absolute',
              bottom: '-12px',
              right: '-12px',
              backgroundColor: '#C61F2C',
              color: 'white',
              padding: '10px 24px',
              borderRadius: '8px',
              fontWeight: '700',
              fontSize: isMobile ? '0.8rem' : '0.9rem',
              boxShadow: '0 4px 16px rgba(198, 31, 44, 0.25)',
              letterSpacing: '0.5px'
            }}>
              PLM TRAINING PROGRAM
            </div>
          </div>
        </Col>
        <Col lg={6}>
          <h2 style={{ 
            color: '#1B1B1B', 
            fontWeight: '700', 
            fontSize: isMobile ? '1.5rem' : '2.2rem',
            marginBottom: '16px'
          }}>
            Your Partner in <span style={{ color: '#C61F2C' }}>Digital Transformation</span>
          </h2>
          <p style={{ color: '#555555', fontSize: '1rem', lineHeight: '1.8' }}>
            We specialize in Product Lifecycle Management (PLM) consulting, training, and implementation. 
            Our team of certified professionals helps organizations streamline product development, 
            reduce time-to-market, and improve collaboration across the entire product lifecycle.
          </p>
          <p style={{ color: '#555555', fontSize: '1rem', lineHeight: '1.8', marginTop: '12px' }}>
            With deep expertise in Siemens Teamcenter, NX, and other industry-leading PLM platforms, 
            we deliver tailored solutions that align with your business goals and drive measurable results 
            across engineering, manufacturing, and supply chain operations.
          </p>
          <div className="d-flex flex-wrap gap-4 mt-4">
            <div className="d-flex align-items-center gap-2">
              <div style={{
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                backgroundColor: '#0F2D62',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '12px',
                fontWeight: '700'
              }}>✓</div>
              <span style={{ color: '#1B1B1B', fontWeight: '500' }}>98% Client Satisfaction</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div style={{
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                backgroundColor: '#0F2D62',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '12px',
                fontWeight: '700'
              }}>✓</div>
              <span style={{ color: '#1B1B1B', fontWeight: '500' }}>30+ Years of Collective Experience</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div style={{
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                backgroundColor: '#0F2D62',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '12px',
                fontWeight: '700'
              }}>✓</div>
              <span style={{ color: '#1B1B1B', fontWeight: '500' }}>Numerous Professionals Trained</span>
            </div>
          </div>
          <Button 
            className="mt-4"
            onClick={() => scrollToSection('services')}
            style={{ 
              backgroundColor: '#C61F2C', 
              border: 'none', 
              borderRadius: '8px', 
              padding: '14px 32px', 
              color: 'white', 
              fontWeight: '600',
              fontSize: '0.95rem',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#0F2D62';
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 8px 24px rgba(15, 45, 98, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#C61F2C';
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
            }}
          >
            Explore Our Services →
          </Button>
        </Col>
      </Row>
    </Container>
  </section>

  {/* ===== WHY CHOOSE A1MARKETSENSE SECTION ===== */}
  <section className="py-5" style={{ 
    backgroundColor: '#F8F9FB'
  }}>
    <Container>
      <Row className="justify-content-center mb-5">
        <Col lg={10} className="text-center">
          <div className="d-inline-block mb-3">
            <div className="d-flex align-items-center gap-2">
              <div style={{ width: '40px', height: '2px', backgroundColor: '#C61F2C' }}></div>
              <span className="text-uppercase fw-bold" style={{ color: '#C61F2C', letterSpacing: '2px', fontSize: isMobile ? '0.7rem' : '0.8rem' }}>Why Choose Us</span>
              <div style={{ width: '40px', height: '2px', backgroundColor: '#C61F2C' }}></div>
            </div>
          </div>
          <h2 className="display-5 fw-bold mb-3" style={{ color: darkMode ? '#F1F5F9' : '#1B1B1B' }}>
            Why Choose <span style={{ color: '#C61F2C' }}>A1MarketSense?</span>
          </h2>
          <p className="lead" style={{ 
            color: darkMode ? '#94A3B8' : '#555555',
            maxWidth: '700px',
            margin: '0 auto',
            fontSize: isMobile ? '1rem' : '1.15rem'
          }}>
            We combine deep PLM expertise with a client-first approach to deliver transformative results.
          </p>
        </Col>
      </Row>

      <Row className="g-4">
        {/* Card 1 - Industry-Focused PLM Expertise */}
        <Col lg={3} md={6}>
          <div className="p-4 rounded-4 h-100" style={{
            backgroundColor: darkMode ? '#1E293B' : '#FFFFFF',
            border: darkMode ? '1px solid #334155' : '1px solid #E8E8E8',
            borderRadius: '16px',
            transition: 'all 0.3s ease',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
            textAlign: 'center'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-8px)';
            e.currentTarget.style.boxShadow = '0 16px 48px rgba(0,0,0,0.08)';
            e.currentTarget.style.borderColor = '#C61F2C';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.03)';
            e.currentTarget.style.borderColor = darkMode ? '#334155' : '#E8E8E8';
          }}>
            {/* Image */}
            <div style={{
              width: '100%',
              height: '180px',
              borderRadius: '12px',
              overflow: 'hidden',
              marginBottom: '16px',
              backgroundColor: '#F8F9FB'
            }}>
              <img 
                src="/about1.png" 
                alt="Industry-Focused PLM Expertise" 
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: 'rgba(198, 31, 44, 0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '-40px auto 16px',
              position: 'relative',
              zIndex: 1,
              border: '3px solid #FFFFFF'
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#C61F2C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                <path d="M2 17l10 5 10-5"/>
                <path d="M2 12l10 5 10-5"/>
              </svg>
            </div>
            <h4 className="fw-bold mb-3" style={{ color: darkMode ? '#F1F5F9' : '#1B1B1B', fontSize: '1.1rem' }}>
              Industry-Focused PLM Expertise
            </h4>
            <p style={{ color: darkMode ? '#94A3B8' : '#555555', fontSize: '0.95rem', lineHeight: '1.7' }}>
              Our experienced PLM consultants help organizations streamline product development, 
              improve collaboration, and implement scalable Siemens Teamcenter solutions tailored to business needs.
            </p>
          </div>
        </Col>

        {/* Card 2 - End-to-End Implementation */}
        <Col lg={3} md={6}>
          <div className="p-4 rounded-4 h-100" style={{
            backgroundColor: darkMode ? '#1E293B' : '#FFFFFF',
            border: darkMode ? '1px solid #334155' : '1px solid #E8E8E8',
            borderRadius: '16px',
            transition: 'all 0.3s ease',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
            textAlign: 'center'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-8px)';
            e.currentTarget.style.boxShadow = '0 16px 48px rgba(0,0,0,0.08)';
            e.currentTarget.style.borderColor = '#C61F2C';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.03)';
            e.currentTarget.style.borderColor = darkMode ? '#334155' : '#E8E8E8';
          }}>
            {/* Image */}
            <div style={{
              width: '100%',
              height: '180px',
              borderRadius: '12px',
              overflow: 'hidden',
              marginBottom: '16px',
              backgroundColor: '#F8F9FB'
            }}>
              <img 
                src="/about2.png" 
                alt="End-to-End Implementation" 
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: 'rgba(198, 31, 44, 0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '-40px auto 16px',
              position: 'relative',
              zIndex: 1,
              border: '3px solid #FFFFFF'
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#C61F2C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="20" rx="2.18"/>
                <line x1="2" y1="9" x2="22" y2="9"/>
                <line x1="2" y1="15" x2="22" y2="15"/>
                <line x1="9" y1="9" x2="9" y2="22"/>
                <line x1="15" y1="9" x2="15" y2="22"/>
              </svg>
            </div>
            <h4 className="fw-bold mb-3" style={{ color: darkMode ? '#F1F5F9' : '#1B1B1B', fontSize: '1.1rem' }}>
              End-to-End Implementation
            </h4>
            <p style={{ color: darkMode ? '#94A3B8' : '#555555', fontSize: '0.95rem', lineHeight: '1.7' }}>
              From requirement analysis and implementation to customization, migration, integration, 
              and deployment, we provide complete PLM services under one roof with a quality-driven approach.
            </p>
          </div>
        </Col>

        {/* Card 3 - Training & Dedicated Support */}
        <Col lg={3} md={6}>
          <div className="p-4 rounded-4 h-100" style={{
            backgroundColor: darkMode ? '#1E293B' : '#FFFFFF',
            border: darkMode ? '1px solid #334155' : '1px solid #E8E8E8',
            borderRadius: '16px',
            transition: 'all 0.3s ease',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
            textAlign: 'center'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-8px)';
            e.currentTarget.style.boxShadow = '0 16px 48px rgba(0,0,0,0.08)';
            e.currentTarget.style.borderColor = '#C61F2C';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.03)';
            e.currentTarget.style.borderColor = darkMode ? '#334155' : '#E8E8E8';
          }}>
            {/* Image */}
            <div style={{
              width: '100%',
              height: '180px',
              borderRadius: '12px',
              overflow: 'hidden',
              marginBottom: '16px',
              backgroundColor: '#F8F9FB'
            }}>
              <img 
                src="/about3.png" 
                alt="Training & Dedicated Support" 
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: 'rgba(198, 31, 44, 0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '-40px auto 16px',
              position: 'relative',
              zIndex: 1,
              border: '3px solid #FFFFFF'
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#C61F2C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                <path d="M8 10h.01"/>
                <path d="M12 10h.01"/>
                <path d="M16 10h.01"/>
              </svg>
            </div>
            <h4 className="fw-bold mb-3" style={{ color: darkMode ? '#F1F5F9' : '#1B1B1B', fontSize: '1.1rem' }}>
              Training & Dedicated Support
            </h4>
            <p style={{ color: darkMode ? '#94A3B8' : '#555555', fontSize: '0.95rem', lineHeight: '1.7' }}>
              We empower your teams with practical training, user adoption programs, ongoing technical 
              support, and post-implementation assistance to maximize your PLM investment.
            </p>
          </div>
        </Col>

        {/* Card 4 - Reliable Delivery & Long-Term Partnership */}
        <Col lg={3} md={6}>
          <div className="p-4 rounded-4 h-100" style={{
            backgroundColor: darkMode ? '#1E293B' : '#FFFFFF',
            border: darkMode ? '1px solid #334155' : '1px solid #E8E8E8',
            borderRadius: '16px',
            transition: 'all 0.3s ease',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
            textAlign: 'center'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-8px)';
            e.currentTarget.style.boxShadow = '0 16px 48px rgba(0,0,0,0.08)';
            e.currentTarget.style.borderColor = '#C61F2C';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.03)';
            e.currentTarget.style.borderColor = darkMode ? '#334155' : '#E8E8E8';
          }}>
            {/* Image */}
            <div style={{
              width: '100%',
              height: '180px',
              borderRadius: '12px',
              overflow: 'hidden',
              marginBottom: '16px',
              backgroundColor: '#F8F9FB'
            }}>
              <img 
                src="/about4.png" 
                alt="Reliable Delivery & Long-Term Partnership" 
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: 'rgba(198, 31, 44, 0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '-40px auto 16px',
              position: 'relative',
              zIndex: 1,
              border: '3px solid #FFFFFF'
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#C61F2C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                <path d="M9 12l2 2 4-4"/>
              </svg>
            </div>
            <h4 className="fw-bold mb-3" style={{ color: darkMode ? '#F1F5F9' : '#1B1B1B', fontSize: '1.1rem' }}>
              Reliable Delivery & Long-Term Partnership
            </h4>
            <p style={{ color: darkMode ? '#94A3B8' : '#555555', fontSize: '0.95rem', lineHeight: '1.7' }}>
              We believe in building lasting client relationships by delivering projects on time, 
              maintaining high-quality standards, ensuring transparent communication, and providing 
              continuous support for future growth.
            </p>
          </div>
        </Col>
      </Row>
    </Container>
  </section>


   {/* ===== SERVICES SECTION ===== */}
  <section id="services" className="py-5" style={{ 
    backgroundColor: '#FFFFFF',
    paddingTop: '80px',
    paddingBottom: '80px'
  }}>
    <Container>
      {/* Section Header */}
      <Row className="justify-content-center mb-5">
        <Col lg={10} className="text-center">
          <div className="d-inline-block mb-3">
            <div className="d-flex align-items-center gap-3">
              <div style={{ width: '50px', height: '2px', backgroundColor: '#C61F2C' }}></div>
              <span className="text-uppercase fw-bold" style={{ color: '#C61F2C', letterSpacing: '3px', fontSize: isMobile ? '0.7rem' : '0.8rem' }}>Our Services</span>
              <div style={{ width: '50px', height: '2px', backgroundColor: '#C61F2C' }}></div>
            </div>
          </div>
          <h2 className="display-4 fw-bold mb-3" style={{ color: '#0F2D62', fontSize: isMobile ? '2.2rem' : '3.2rem' }}>
            PLM & IT <span style={{ color: '#C61F2C' }}>Solutions</span>
          </h2>
          <p className="lead" style={{ 
            color: '#555555',
            maxWidth: '750px',
            margin: '0 auto',
            fontSize: isMobile ? '1rem' : '1.15rem',
            fontWeight: '400',
            lineHeight: '1.7'
          }}>
            End-to-end PLM and IT services designed to streamline processes, drive innovation, and maximize business value.
          </p>
        </Col>
      </Row>

      {/* Services Grid - 3 columns on desktop */}
      <Row className="g-4">
        {/* Service 1 - PLM Training & Enablement */}
        <Col lg={4} md={6}>
          <div className="p-4 rounded-4 h-100 d-flex flex-column" style={{
            backgroundColor: '#FFFFFF',
            border: '1px solid #E8E8E8',
            borderRadius: '18px',
            transition: 'all 0.3s ease',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-8px)';
            e.currentTarget.style.boxShadow = '0 16px 48px rgba(0,0,0,0.08)';
            e.currentTarget.style.borderColor = '#C61F2C';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.04)';
            e.currentTarget.style.borderColor = '#E8E8E8';
          }}>
            {/* Icon + Title Row */}
            <div className="d-flex align-items-center gap-3 mb-2">
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '12px',
                backgroundColor: 'rgba(198, 31, 44, 0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <svg width="70" height="70" viewBox="0 0 64 64" fill="none">
    <circle cx="32" cy="32" r="30" fill="#F5F8FF"/>
    <path d="M12 24L32 14L52 24L32 34L12 24Z" fill="#0F2D62"/>
    <path d="M18 30V39C18 44 24 48 32 48C40 48 46 44 46 39V30" stroke="#0F2D62" strokeWidth="2.5"/>
    <circle cx="50" cy="27" r="2.5" fill="#C61F2C"/>
    <path d="M50 29V39" stroke="#C61F2C" strokeWidth="2"/>
  </svg>
              </div>
              <h4 className="fw-bold mb-0" style={{ color: '#0F2D62', fontSize: '1.1rem' }}>
                PLM Training & Enablement
                {/* Red Underline */}
            <div style={{ 
              width: '50px', 
              height: '4px', 
              backgroundColor: '#C61F2C', 
              marginBottom: '12px',
              marginTop:'10px',
              borderRadius: '2px'
            }}></div>
              </h4>
            </div>
            
            
            
            <p style={{ color: '#555555', fontSize: '0.9rem', lineHeight: '1.7', marginBottom: '14px' }}>
              Empower your teams with industry-leading training programs and hands-on learning experiences.
            </p>
            
            <ul className="list-unstyled mt-auto" style={{ marginBottom: '0' }}>
              <li className="d-flex align-items-center gap-2 mb-2">
                <div style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  backgroundColor: '#0F2D62',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '10px',
                  fontWeight: '700',
                  flexShrink: 0
                }}>✓</div>
                <span style={{ color: '#555555', fontSize: '0.85rem' }}>Role-based Training Programs</span>
              </li>
              <li className="d-flex align-items-center gap-2 mb-2">
                <div style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  backgroundColor: '#0F2D62',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '10px',
                  fontWeight: '700',
                  flexShrink: 0
                }}>✓</div>
                <span style={{ color: '#555555', fontSize: '0.85rem' }}>Hands-on Labs & Workshops</span>
              </li>
              <li className="d-flex align-items-center gap-2">
                <div style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  backgroundColor: '#0F2D62',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '10px',
                  fontWeight: '700',
                  flexShrink: 0
                }}>✓</div>
                <span style={{ color: '#555555', fontSize: '0.85rem' }}>Certification & Career Support</span>
              </li>
            </ul>
          </div>
        </Col>

        {/* Service 2 - PLM Consulting */}
        <Col lg={4} md={6}>
          <div className="p-4 rounded-4 h-100 d-flex flex-column" style={{
            backgroundColor: '#FFFFFF',
            border: '1px solid #E8E8E8',
            borderRadius: '18px',
            transition: 'all 0.3s ease',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-8px)';
            e.currentTarget.style.boxShadow = '0 16px 48px rgba(0,0,0,0.08)';
            e.currentTarget.style.borderColor = '#C61F2C';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.04)';
            e.currentTarget.style.borderColor = '#E8E8E8';
          }}>
            {/* Icon + Title Row */}
            <div className="d-flex align-items-center gap-3 mb-2">
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '12px',
                backgroundColor: 'rgba(198, 31, 44, 0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <svg width="70" height="70" viewBox="0 0 64 64" fill="none">
    <circle cx="32" cy="32" r="30" fill="#F5F8FF"/>
    <circle cx="32" cy="32" r="14" stroke="#0F2D62" strokeWidth="4"/>
    <circle cx="32" cy="32" r="7" stroke="#C61F2C" strokeWidth="4"/>
    <circle cx="32" cy="32" r="2.5" fill="#0F2D62"/>
    <path d="M37 27L48 16" stroke="#C61F2C" strokeWidth="4" strokeLinecap="round"/>
    <path d="M48 16L45 25L39 19L48 16Z" fill="#C61F2C"/>
  </svg>
              </div>
              <h4 className="fw-bold mb-0" style={{ color: '#0F2D62', fontSize: '1.1rem' }}>
                PLM Consulting
                  {/* Red Underline */}
            <div style={{ 
              width: '50px', 
              height: '4px', 
              backgroundColor: '#C61F2C', 
              marginBottom: '12px',
              marginTop:'10px',
              borderRadius: '2px'
            }}></div>
              </h4>
            </div>
            
          
            
            <p style={{ color: '#555555', fontSize: '0.9rem', lineHeight: '1.7', marginBottom: '14px' }}>
              Strategic consulting to help you define the right PLM roadmap and achieve measurable business outcomes.
            </p>
            
            <ul className="list-unstyled mt-auto" style={{ marginBottom: '0' }}>
              <li className="d-flex align-items-center gap-2 mb-2">
                <div style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  backgroundColor: '#0F2D62',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '10px',
                  fontWeight: '700',
                  flexShrink: 0
                }}>✓</div>
                <span style={{ color: '#555555', fontSize: '0.85rem' }}>PLM Strategy & Roadmap</span>
              </li>
              <li className="d-flex align-items-center gap-2 mb-2">
                <div style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  backgroundColor: '#0F2D62',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '10px',
                  fontWeight: '700',
                  flexShrink: 0
                }}>✓</div>
                <span style={{ color: '#555555', fontSize: '0.85rem' }}>Process Assessment & Optimization</span>
              </li>
              <li className="d-flex align-items-center gap-2">
                <div style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  backgroundColor: '#0F2D62',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '10px',
                  fontWeight: '700',
                  flexShrink: 0
                }}>✓</div>
                <span style={{ color: '#555555', fontSize: '0.85rem' }}>ROI Analysis & Business Case</span>
              </li>
            </ul>
          </div>
        </Col>

        {/* Service 3 - PLM Implementation */}
        <Col lg={4} md={6}>
          <div className="p-4 rounded-4 h-100 d-flex flex-column" style={{
            backgroundColor: '#FFFFFF',
            border: '1px solid #E8E8E8',
            borderRadius: '18px',
            transition: 'all 0.3s ease',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-8px)';
            e.currentTarget.style.boxShadow = '0 16px 48px rgba(0,0,0,0.08)';
            e.currentTarget.style.borderColor = '#C61F2C';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.04)';
            e.currentTarget.style.borderColor = '#E8E8E8';
          }}>
            {/* Icon + Title Row */}
            <div className="d-flex align-items-center gap-3 mb-2">
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '12px',
                backgroundColor: 'rgba(198, 31, 44, 0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <svg width="70" height="70" viewBox="0 0 64 64" fill="none">
    <circle cx="32" cy="32" r="30" fill="#F5F8FF"/>
    <rect x="14" y="16" width="36" height="24" rx="2" stroke="#0F2D62" strokeWidth="3"/>
    <path d="M24 48H40" stroke="#0F2D62" strokeWidth="3"/>
    <path d="M32 40V48" stroke="#0F2D62" strokeWidth="3"/>
    <circle cx="40" cy="28" r="6" fill="#C61F2C"/>
    <path d="M40 22V34M34 28H46M36 24L44 32M44 24L36 32" stroke="white" strokeWidth="2"/>
  </svg>
              </div>
              <h4 className="fw-bold mb-0" style={{ color: '#0F2D62', fontSize: '1.1rem' }}>
                PLM Implementation
                {/* Red Underline */}
            <div style={{ 
              width: '50px', 
              height: '4px', 
              backgroundColor: '#C61F2C', 
              marginBottom: '12px',
              marginTop:'10px',
              borderRadius: '2px'
            }}></div>
              </h4>
            </div>
            
            {/*  */}
            
            <p style={{ color: '#555555', fontSize: '0.9rem', lineHeight: '1.7', marginBottom: '14px' }}>
              Seamless implementation of PLM solutions tailored to your business processes and objectives.
            </p>
            
            <ul className="list-unstyled mt-auto" style={{ marginBottom: '0' }}>
              <li className="d-flex align-items-center gap-2 mb-2">
                <div style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  backgroundColor: '#0F2D62',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '10px',
                  fontWeight: '700',
                  flexShrink: 0
                }}>✓</div>
                <span style={{ color: '#555555', fontSize: '0.85rem' }}>Requirement Analysis</span>
              </li>
              <li className="d-flex align-items-center gap-2 mb-2">
                <div style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  backgroundColor: '#0F2D62',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '10px',
                  fontWeight: '700',
                  flexShrink: 0
                }}>✓</div>
                <span style={{ color: '#555555', fontSize: '0.85rem' }}>Solution Design & Configuration</span>
              </li>
              <li className="d-flex align-items-center gap-2">
                <div style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  backgroundColor: '#0F2D62',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '10px',
                  fontWeight: '700',
                  flexShrink: 0
                }}>✓</div>
                <span style={{ color: '#555555', fontSize: '0.85rem' }}>Implementation & Go-Live Support</span>
              </li>
            </ul>
          </div>
        </Col>

        {/* Service 4 - Teamcenter Services */}
        <Col lg={4} md={6}>
          <div className="p-4 rounded-4 h-100 d-flex flex-column" style={{
            backgroundColor: '#FFFFFF',
            border: '1px solid #E8E8E8',
            borderRadius: '18px',
            transition: 'all 0.3s ease',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-8px)';
            e.currentTarget.style.boxShadow = '0 16px 48px rgba(0,0,0,0.08)';
            e.currentTarget.style.borderColor = '#C61F2C';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.04)';
            e.currentTarget.style.borderColor = '#E8E8E8';
          }}>
            {/* Icon + Title Row */}
            <div className="d-flex align-items-center gap-3 mb-2">
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '12px',
                backgroundColor: 'rgba(198, 31, 44, 0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <svg width="70" height="70" viewBox="0 0 64 64" fill="none">
    <circle cx="32" cy="32" r="30" fill="#F5F8FF"/>
    <path d="M18 22L32 16L46 22L32 28L18 22Z" fill="#C61F2C"/>
    <path d="M18 30L32 24L46 30L32 36L18 30Z" fill="#0F2D62"/>
    <path d="M18 38L32 32L46 38L32 44L18 38Z" fill="#C61F2C"/>
  </svg>
              </div>
              <h4 className="fw-bold mb-0" style={{ color: '#0F2D62', fontSize: '1.1rem' }}>
                Teamcenter Services
               
            <div style={{ 
              width: '50px', 
              height: '4px', 
              backgroundColor: '#C61F2C', 
              marginBottom: '12px',
              marginTop:'10px',
              borderRadius: '2px'
            }}></div>
              </h4>
            </div>
            
            {/* Red Underline
            <div style={{ 
              width: '50px', 
              height: '3px', 
              backgroundColor: '#C61F2C', 
              marginBottom: '12px',
              borderRadius: '2px'
            }}></div> */}
            
            <p style={{ color: '#555555', fontSize: '0.9rem', lineHeight: '1.7', marginBottom: '14px' }}>
              Comprehensive Siemens Teamcenter services to manage the entire product lifecycle efficiently.
            </p>
            
            <ul className="list-unstyled mt-auto" style={{ marginBottom: '0' }}>
              <li className="d-flex align-items-center gap-2 mb-2">
                <div style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  backgroundColor: '#0F2D62',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '10px',
                  fontWeight: '700',
                  flexShrink: 0
                }}>✓</div>
                <span style={{ color: '#555555', fontSize: '0.85rem' }}>Teamcenter Implementation</span>
              </li>
              <li className="d-flex align-items-center gap-2 mb-2">
                <div style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  backgroundColor: '#0F2D62',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '10px',
                  fontWeight: '700',
                  flexShrink: 0
                }}>✓</div>
                <span style={{ color: '#555555', fontSize: '0.85rem' }}>Customization & Development</span>
              </li>
              <li className="d-flex align-items-center gap-2">
                <div style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  backgroundColor: '#0F2D62',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '10px',
                  fontWeight: '700',
                  flexShrink: 0
                }}>✓</div>
                <span style={{ color: '#555555', fontSize: '0.85rem' }}>System Administration & Support</span>
              </li>
            </ul>
          </div>
        </Col>

        {/* Service 5 - Integration & Migration */}
        <Col lg={4} md={6}>
          <div className="p-4 rounded-4 h-100 d-flex flex-column" style={{
            backgroundColor: '#FFFFFF',
            border: '1px solid #E8E8E8',
            borderRadius: '18px',
            transition: 'all 0.3s ease',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-8px)';
            e.currentTarget.style.boxShadow = '0 16px 48px rgba(0,0,0,0.08)';
            e.currentTarget.style.borderColor = '#C61F2C';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.04)';
            e.currentTarget.style.borderColor = '#E8E8E8';
          }}>
            {/* Icon + Title Row */}
            <div className="d-flex align-items-center gap-3 mb-2">
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '12px',
                backgroundColor: 'rgba(198, 31, 44, 0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <svg width="70" height="70" viewBox="0 0 64 64" fill="none">
    <circle cx="32" cy="32" r="30" fill="#F5F8FF"/>
    <path d="M22 38C17 38 14 35 14 30C14 25 18 22 22 22C24 17 29 14 35 14C43 14 49 20 49 28C53 29 56 33 56 38C56 43 52 46 47 46H22" fill="#0F2D62"/>
    <path d="M32 46V26" stroke="#C61F2C" strokeWidth="3"/>
    <path d="M26 32L32 26L38 32" stroke="#C61F2C" strokeWidth="3"/>
  </svg>
              </div>
              <h4 className="fw-bold mb-0" style={{ color: '#0F2D62', fontSize: '1.1rem' }}>
                Integration & Migration
                
            <div style={{ 
              width: '50px', 
              height: '4px', 
              backgroundColor: '#C61F2C', 
              marginBottom: '12px',
              marginTop:'10px',
              borderRadius: '2px'
            }}></div>
              </h4>
            </div>
            
            
            
            <p style={{ color: '#555555', fontSize: '0.9rem', lineHeight: '1.7', marginBottom: '14px' }}>
              Integrate PLM with enterprise systems and migrate data seamlessly with zero disruption.
            </p>
            
            <ul className="list-unstyled mt-auto" style={{ marginBottom: '0' }}>
              <li className="d-flex align-items-center gap-2 mb-2">
                <div style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  backgroundColor: '#0F2D62',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '10px',
                  fontWeight: '700',
                  flexShrink: 0
                }}>✓</div>
                <span style={{ color: '#555555', fontSize: '0.85rem' }}>Data Migration & Cleansing</span>
              </li>
              <li className="d-flex align-items-center gap-2 mb-2">
                <div style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  backgroundColor: '#0F2D62',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '10px',
                  fontWeight: '700',
                  flexShrink: 0
                }}>✓</div>
                <span style={{ color: '#555555', fontSize: '0.85rem' }}>System Integration (ERP, CAD, etc.)</span>
              </li>
              <li className="d-flex align-items-center gap-2">
                <div style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  backgroundColor: '#0F2D62',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '10px',
                  fontWeight: '700',
                  flexShrink: 0
                }}>✓</div>
                <span style={{ color: '#555555', fontSize: '0.85rem' }}>Legacy System Modernization</span>
              </li>
            </ul>
          </div>
        </Col>

        {/* Service 6 - Support & Managed Services */}
        <Col lg={4} md={6}>
          <div className="p-4 rounded-4 h-100 d-flex flex-column" style={{
            backgroundColor: '#FFFFFF',
            border: '1px solid #E8E8E8',
            borderRadius: '18px',
            transition: 'all 0.3s ease',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-8px)';
            e.currentTarget.style.boxShadow = '0 16px 48px rgba(0,0,0,0.08)';
            e.currentTarget.style.borderColor = '#C61F2C';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.04)';
            e.currentTarget.style.borderColor = '#E8E8E8';
          }}>
            {/* Icon + Title Row */}
            <div className="d-flex align-items-center gap-3 mb-2">
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '12px',
                backgroundColor: 'rgba(198, 31, 44, 0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <svg width="70" height="70" viewBox="0 0 64 64" fill="none">
    <circle cx="32" cy="32" r="30" fill="#F5F8FF"/>
    <path d="M18 34V30C18 22 24 16 32 16C40 16 46 22 46 30V34" stroke="#0F2D62" strokeWidth="4"/>
    <rect x="14" y="30" width="8" height="12" rx="3" fill="#0F2D62"/>
    <rect x="42" y="30" width="8" height="12" rx="3" fill="#0F2D62"/>
    <path d="M46 42C46 46 42 50 36 50H30" stroke="#0F2D62" strokeWidth="3"/>
    <circle cx="28" cy="50" r="3" fill="#C61F2C"/>
  </svg>
              </div>
              <h4 className="fw-bold mb-0" style={{ color: '#0F2D62', fontSize: '1.1rem' }}>
                Support & Managed Services
                 {/* Red Underline */}
            <div style={{ 
              width: '50px', 
              height: '4px', 
              backgroundColor: '#C61F2C', 
              marginBottom: '12px',
              marginTop:'10px',
              borderRadius: '2px'
            }}></div>
              </h4>
            </div>
            
            {/* Red Underline
            <div style={{ 
              width: '50px', 
              height: '3px', 
              backgroundColor: '#C61F2C', 
              marginBottom: '12px',
              borderRadius: '2px'
            }}></div> */}
            
            <p style={{ color: '#555555', fontSize: '0.9rem', lineHeight: '1.7', marginBottom: '14px' }}>
              Reliable support and managed services to ensure peak performance and business continuity.
            </p>
            
            <ul className="list-unstyled mt-auto" style={{ marginBottom: '0' }}>
              <li className="d-flex align-items-center gap-2 mb-2">
                <div style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  backgroundColor: '#0F2D62',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '10px',
                  fontWeight: '700',
                  flexShrink: 0
                }}>✓</div>
                <span style={{ color: '#555555', fontSize: '0.85rem' }}>L1 / L2 / L3 Support</span>
              </li>
              <li className="d-flex align-items-center gap-2 mb-2">
                <div style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  backgroundColor: '#0F2D62',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '10px',
                  fontWeight: '700',
                  flexShrink: 0
                }}>✓</div>
                <span style={{ color: '#555555', fontSize: '0.85rem' }}>System Health & Monitoring</span>
              </li>
              <li className="d-flex align-items-center gap-2">
                <div style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  backgroundColor: '#0F2D62',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '10px',
                  fontWeight: '700',
                  flexShrink: 0
                }}>✓</div>
                <span style={{ color: '#555555', fontSize: '0.85rem' }}>Enhancements & Upgrades</span>
              </li>
            </ul>
          </div>
        </Col>
      </Row>

    

      {/* CTA Button */}
      <Row className="mt-5">
        <Col className="text-center">
          <CalendlyPopupButton
            url="https://calendly.com/sahilumate32/30min"
            text="📅 Schedule a Consultation"
            className="calendly-btn"
            style={{ 
              backgroundColor: '#C61F2C', 
              border: 'none', 
              borderRadius: '8px', 
              color: 'white', 
              fontWeight: '600', 
              padding: '14px 40px',
              fontSize: '1rem',
              display: 'inline-block',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#0F2D62';
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 8px 24px rgba(15, 45, 98, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#C61F2C';
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
            }}
          />
        </Col>
      </Row>
    </Container>
  </section>

  


         {/* ===== CONTACT SECTION ===== */}
  <section id="contact" className="py-5" style={{ 
    backgroundColor: '#F8F9FB',
    paddingTop: '80px',
    paddingBottom: '80px'
  }}>
    <Container>
      {/* Section Header */}
      <Row className="justify-content-center mb-4">
        <Col lg={10} className="text-center">
          <div className="d-inline-block mb-3">
            <div className="d-flex align-items-center gap-3">
              <div style={{ width: '50px', height: '2px', backgroundColor: '#C61F2C' }}></div>
              <span className="text-uppercase fw-bold" style={{ color: '#C61F2C', letterSpacing: '3px', fontSize: isMobile ? '0.7rem' : '0.8rem' }}>Get In Touch</span>
              <div style={{ width: '50px', height: '2px', backgroundColor: '#C61F2C' }}></div>
            </div>
          </div>
          <h2 className="display-4 fw-bold mb-3" style={{ color: '#0F2D62', fontSize: isMobile ? '2.2rem' : '3.2rem' }}>
            Let's <span style={{ color: '#C61F2C' }}>Connect</span>
          </h2>
          <p style={{ 
            color: '#555555',
            maxWidth: '700px',
            margin: '0 auto',
            fontSize: isMobile ? '1rem' : '1.15rem',
            fontWeight: '400',
            lineHeight: '1.7'
          }}>
            Have questions about our PLM services? Our team is here to help you find the right solutions for your business.
          </p>
        </Col>
      </Row>

      {/* Contact Form Section - Image Left, Form Right */}
      <Row className="g-4 align-items-stretch">
        {/* LEFT COLUMN - IMAGE */}
        {/* LEFT COLUMN - IMAGE */}
  <Col lg={5}>
    <div
      style={{
        backgroundColor: "#0F2D62",
        borderRadius: "18px",
        overflow: "hidden",
        position: "relative",
        minHeight: isMobile ? "320px" : "580px",
        height: "100%",
        border: "1px solid #E8E8E8",
        boxShadow: "0 15px 40px rgba(15,45,98,.12)"
      }}
    >
      <img
        src="/contactimg.png"
        alt="Contact"
        style={{
          position: "absolute",
          top: "-3%",
          left: "-6%",
          width: "104%",
          height: "106%",
          objectFit: "cover",
          objectPosition: "left center",
          display: "block",
          zIndex: 1
        }}
      />

      {/* Optional dark overlay */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(90deg, rgba(15,45,98,.08) 0%, rgba(15,45,98,0) 55%)",
          zIndex: 2
        }}
      />
    </div>
  </Col>

        {/* RIGHT COLUMN - FORM */}
        <Col lg={7}>
          <Card className="border-0 shadow-sm h-100" style={{ 
            backgroundColor: '#FFFFFF', 
            border: '1px solid #E8E8E8', 
            borderRadius: '16px'
          }}>
            <Card.Body className="p-4 p-md-5">
             {/* Contact Info Cards */}
  <Row className="g-3 mb-4">
    <Col md={4}>
      <div className="text-center p-3 rounded-4 h-100" style={{ 
        backgroundColor: '#FFFFFF',
        border: '2px solid #0F2D62',
        borderLeft: '2px solid #0F2D62',
        borderBottom: '2px solid #0F2D62',
        borderRight: '1px solid #E8E8E8',
        borderTop: '1px solid #E8E8E8',
        borderRadius: '12px',
        transition: 'all 0.3s ease',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-3px)';
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
      }}>
        <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2" style={{ 
          width: '48px', 
          height: '48px', 
          background: 'rgba(15, 45, 98, 0.08)',
        }}>
          <MapPin size={20} color="#0F2D62" />
        </div>
        <h6 className="fw-bold mb-1" style={{ color: '#0F2D62', fontSize: '0.9rem' }}>Visit Us</h6>
        <p style={{ color: '#666', fontSize: '0.8rem', marginBottom: '0' }}>Brampton, Ontario, Canada</p>
      </div>
    </Col>
    
    <Col md={4}>
      <div className="text-center p-3 rounded-4 h-100" style={{ 
        backgroundColor: '#FFFFFF',
        border: '2px solid #C61F2C',
        borderLeft: '2px solid #C61F2C',
        borderBottom: '2px solid #C61F2C',
        borderRight: '1px solid #E8E8E8',
        borderTop: '1px solid #E8E8E8',
        borderRadius: '12px',
        transition: 'all 0.3s ease',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-3px)';
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
      }}>
        <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2" style={{ 
          width: '48px', 
          height: '48px', 
          background: 'rgba(198, 31, 44, 0.08)',
        }}>
          <Mail size={20} color="#C61F2C" />
        </div>
        <h6 className="fw-bold mb-1" style={{ color: '#C61F2C', fontSize: '0.9rem' }}>Email Us</h6>
        <p style={{ color: '#666', fontSize: '0.8rem', marginBottom: '0' }}>contact@a1marketsense.ca</p>
      </div>
    </Col>
    
    <Col md={4}>
      <div className="text-center p-3 rounded-4 h-100" style={{ 
        backgroundColor: '#FFFFFF',
        border: '2px solid #0F2D62',
        borderLeft: '2px solid #0F2D62',
        borderBottom: '2px solid #0F2D62',
        borderRight: '1px solid #E8E8E8',
        borderTop: '1px solid #E8E8E8',
        borderRadius: '12px',
        transition: 'all 0.3s ease',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-3px)';
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
      }}>
        <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2" style={{ 
          width: '48px', 
          height: '48px', 
          background: 'rgba(15, 45, 98, 0.08)',
        }}>
          <Phone size={20} color="#0F2D62" />
        </div>
        <h6 className="fw-bold mb-1" style={{ color: '#0F2D62', fontSize: '0.9rem' }}>Call Us</h6>
        <p style={{ color: '#666', fontSize: '0.8rem', marginBottom: '0' }}>+1 (647) 977-4503</p>
      </div>
    </Col>
  </Row>

              {/* Form Header */}
              <div className="mb-3">
                <h5 className="fw-bold mb-1" style={{ color: '#0F2D62' }}>Send Us a Message</h5>
                <p style={{ color: '#555555', fontSize: '0.85rem', marginBottom: '0' }}>
                  We're Here to Help You Succeed.
                </p>
              </div>
              
              {status.msg && (
                <Alert variant={status.type} style={{ 
                  borderRadius: '8px',
                  backgroundColor: status.type === 'success' ? '#10b981' : '#ef4444',
                  color: 'white',
                  border: 'none',
                  fontSize: '0.85rem',
                  padding: '8px 12px'
                }}>
                  {status.msg}
                </Alert>
              )}
              
              <Form ref={form} onSubmit={sendEmail}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <Form.Label className="small fw-bold" style={{ color: '#0F2D62', fontSize: '0.75rem' }}>Your Name</Form.Label>
                      <Form.Control 
                        name="from_name" 
                        required 
                        placeholder="John Doe" 
                        style={{ 
                          backgroundColor: '#F8F9FB', 
                          borderColor: '#E8E8E8', 
                          color: '#1B1B1B',
                          borderRadius: '8px',
                          padding: '10px 12px',
                          fontSize: '0.9rem'
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <Form.Label className="small fw-bold" style={{ color: '#0F2D62', fontSize: '0.75rem' }}>Email Address</Form.Label>
                      <Form.Control 
                        name="reply_to" 
                        type="email" 
                        required 
                        placeholder="john@example.com"
                        style={{ 
                          backgroundColor: '#F8F9FB', 
                          borderColor: '#E8E8E8', 
                          color: '#1B1B1B',
                          borderRadius: '8px',
                          padding: '10px 12px',
                          fontSize: '0.9rem'
                        }}
                      />
                    </Form.Group>
                  </Col>
                </Row>
             
                <Form.Group className="mb-2">
                  <Form.Label className="small fw-bold" style={{ color: '#0F2D62', fontSize: '0.75rem' }}>Phone Number</Form.Label>
                  <Form.Control 
                    name="phone_number" 
                    type="tel" 
                    required 
                    placeholder="(123) 456-7890"
                    style={{ 
                      backgroundColor: '#F8F9FB', 
                      borderColor: '#E8E8E8', 
                      color: '#1B1B1B',
                      borderRadius: '8px',
                      padding: '10px 12px',
                      fontSize: '0.9rem'
                    }}
                  />
                </Form.Group>

                <Form.Group className="mb-2">
                  <Form.Label className="small fw-bold" style={{ color: '#0F2D62', fontSize: '0.75rem' }}>Service Interested In</Form.Label>
                  <Form.Select 
                    name="service_interest"
                    style={{ 
                      backgroundColor: '#F8F9FB', 
                      borderColor: '#E8E8E8', 
                      color: '#1B1B1B',
                      borderRadius: '8px',
                      padding: '10px 12px',
                      fontSize: '0.9rem'
                    }}
                  >
                    <option value="">Select a Service</option>
                    <option value="PLM Training">PLM Training</option>
                    <option value="PLM Consulting">PLM Consulting</option>
                    <option value="Teamcenter Services">Teamcenter Services</option>
                    <option value="IT Infrastructure">IT Infrastructure</option>
                    <option value="Other">Other</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold" style={{ color: '#0F2D62', fontSize: '0.75rem' }}>Your Message</Form.Label>
                  <Form.Control 
                    name="message" 
                    as="textarea" 
                    rows={3} 
                    required 
                    placeholder="Tell us about your requirements..."
                    style={{ 
                      backgroundColor: '#F8F9FB', 
                      borderColor: '#E8E8E8', 
                      color: '#1B1B1B',
                      borderRadius: '8px',
                      padding: '10px 12px',
                      fontSize: '0.9rem'
                    }}
                  />
                </Form.Group>

                <div className="d-grid">
                  <Button 
                    type="submit" 
                    className="fw-bold py-2" 
                    style={{ 
                      backgroundColor: '#C61F2C', 
                      border: 'none', 
                      color: 'white',
                      borderRadius: '8px',
                      transition: 'all 0.3s ease',
                      fontSize: '0.9rem'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#0F2D62';
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 8px 24px rgba(15, 45, 98, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#C61F2C';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    SEND MESSAGE
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  </section>
  </>

       {/* import React, { useState } from 'react';
  import { Container, Row, Col } from 'react-bootstrap';
  import { MapPin, Phone, Mail, Clock, Instagram, Linkedin, Youtube, Facebook } from 'lucide-react';
  import useCalendly from '../hooks/useCalendly';
  import CalendlyPopup from '../components/CalendlyPopup'; */}

  {/* ===== FOOTER ===== */}
  <footer className="pt-5 pb-4" style={{ 
    backgroundColor: '#F4F6F9', 
    color: '#1B1B1B',
    borderTop: `3px solid #C61F2C`,
    marginTop: '50px'
  }}>
    <Container>
      <Row className="mb-5">
        <Col xs={12}>
          <div style={{ 
            display: 'flex', 
            flexDirection: 'row',
            alignItems: 'center',
            gap: isMobile ? '15px' : '30px',
            marginBottom: '20px'
          }}>
            <img 
              src="/logo5.png" 
              alt="A1 Marketsense" 
              style={{ 
                height: isMobile ? '45px' : '70px', 
                width: 'auto', 
                flexShrink: 0 
              }}
            />
            <p style={{ 
              color: '#333333', 
              lineHeight: isMobile ? '1.4' : '1.6', 
              fontSize: isMobile ? '0.8rem' : '0.95rem', 
              margin: 0,
              textAlign: 'left',
              flex: 1
            }}>
              Professional PLM training, consulting, and IT solutions serving Brampton and across Ontario since 2020.
            </p>
          </div>
          
          <div className="d-flex gap-3" style={{ justifyContent: 'flex-start' }}>
            <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" 
              className="d-flex align-items-center justify-content-center rounded-circle"
              style={{ 
                width: isMobile ? '34px' : '40px', 
                height: isMobile ? '34px' : '40px', 
                backgroundColor: 'rgba(0,0,0,0.06)',
                color: '#333333',
                transition: 'all 0.3s ease',
                textDecoration: 'none'
              }} 
              onMouseEnter={(e) => { 
                e.target.style.backgroundColor = '#C61F2C'; 
                e.target.style.color = 'white';
                e.target.style.transform = 'translateY(-3px)'; 
              }} 
              onMouseLeave={(e) => { 
                e.target.style.backgroundColor = 'rgba(0,0,0,0.06)'; 
                e.target.style.color = '#333333';
                e.target.style.transform = 'translateY(0)'; 
              }}>
              <Instagram size={isMobile ? 16 : 20} />
            </a>
            <a href="https://www.linkedin.com/company/a1-market-sense/posts/?feedView=all" target="_blank" rel="noopener noreferrer" 
              className="d-flex align-items-center justify-content-center rounded-circle"
              style={{ 
                width: isMobile ? '34px' : '40px', 
                height: isMobile ? '34px' : '40px', 
                backgroundColor: 'rgba(0,0,0,0.06)',
                color: '#333333',
                transition: 'all 0.3s ease',
                textDecoration: 'none'
              }} 
              onMouseEnter={(e) => { 
                e.target.style.backgroundColor = '#C61F2C'; 
                e.target.style.color = 'white';
                e.target.style.transform = 'translateY(-3px)'; 
              }} 
              onMouseLeave={(e) => { 
                e.target.style.backgroundColor = 'rgba(0,0,0,0.06)'; 
                e.target.style.color = '#333333';
                e.target.style.transform = 'translateY(0)'; 
              }}>
              <Linkedin size={isMobile ? 16 : 20} />
            </a>
            <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" 
              className="d-flex align-items-center justify-content-center rounded-circle"
              style={{ 
                width: isMobile ? '34px' : '40px', 
                height: isMobile ? '34px' : '40px', 
                backgroundColor: 'rgba(0,0,0,0.06)',
                color: '#333333',
                transition: 'all 0.3s ease',
                textDecoration: 'none'
              }} 
              onMouseEnter={(e) => { 
                e.target.style.backgroundColor = '#C61F2C'; 
                e.target.style.color = 'white';
                e.target.style.transform = 'translateY(-3px)'; 
              }} 
              onMouseLeave={(e) => { 
                e.target.style.backgroundColor = 'rgba(0,0,0,0.06)'; 
                e.target.style.color = '#333333';
                e.target.style.transform = 'translateY(0)'; 
              }}>
              <Youtube size={isMobile ? 16 : 20} />
            </a>
            <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" 
              className="d-flex align-items-center justify-content-center rounded-circle"
              style={{ 
                width: isMobile ? '34px' : '40px', 
                height: isMobile ? '34px' : '40px', 
                backgroundColor: 'rgba(0,0,0,0.06)',
                color: '#333333',
                transition: 'all 0.3s ease',
                textDecoration: 'none'
              }} 
              onMouseEnter={(e) => { 
                e.target.style.backgroundColor = '#C61F2C'; 
                e.target.style.color = 'white';
                e.target.style.transform = 'translateY(-3px)'; 
              }} 
              onMouseLeave={(e) => { 
                e.target.style.backgroundColor = 'rgba(0,0,0,0.06)'; 
                e.target.style.color = '#333333';
                e.target.style.transform = 'translateY(0)'; 
              }}>
              <Facebook size={isMobile ? 16 : 20} />
            </a>
          </div>
        </Col>
      </Row>

      <Row className="g-4">
        <Col xs={6} lg={3}>
          <h5 className="fw-bold mb-3" style={{ color: '#0F2D62', fontSize: isMobile ? '0.95rem' : '1.1rem', textAlign: 'left' }}>Quick Links</h5>
          <ul className="list-unstyled" style={{ textAlign: 'left' }}>
            <li className="mb-2"><a onClick={() => scrollToSection('home')} style={{ color: '#333333', textDecoration: 'none', cursor: 'pointer', fontSize: isMobile ? '0.85rem' : '0.95rem' }} onMouseEnter={(e) => e.target.style.color = '#C61F2C'} onMouseLeave={(e) => e.target.style.color = '#333333'}>Home</a></li>
            <li className="mb-2"><a onClick={() => scrollToSection('about')} style={{ color: '#333333', textDecoration: 'none', cursor: 'pointer', fontSize: isMobile ? '0.85rem' : '0.95rem' }} onMouseEnter={(e) => e.target.style.color = '#C61F2C'} onMouseLeave={(e) => e.target.style.color = '#333333'}>About</a></li>
            <li className="mb-2"><a onClick={() => scrollToSection('services')} style={{ color: '#333333', textDecoration: 'none', cursor: 'pointer', fontSize: isMobile ? '0.85rem' : '0.95rem' }} onMouseEnter={(e) => e.target.style.color = '#C61F2C'} onMouseLeave={(e) => e.target.style.color = '#333333'}>Services</a></li>
            <li className="mb-2"><a onClick={() => scrollToSection('contact')} style={{ color: '#333333', textDecoration: 'none', cursor: 'pointer', fontSize: isMobile ? '0.85rem' : '0.95rem' }} onMouseEnter={(e) => e.target.style.color = '#C61F2C'} onMouseLeave={(e) => e.target.style.color = '#333333'}>Contact</a></li>
          </ul>
        </Col>

        <Col xs={6} lg={3}>
          <h5 className="fw-bold mb-3" style={{ color: '#0F2D62', fontSize: isMobile ? '0.95rem' : '1.1rem', textAlign: 'left' }}>Our Services</h5>
          <ul className="list-unstyled" style={{ textAlign: 'left' }}>
            <li className="mb-2"><span style={{ color: '#333333', fontSize: isMobile ? '0.85rem' : '0.95rem' }}>PLM Training</span></li>
            <li className="mb-2"><span style={{ color: '#333333', fontSize: isMobile ? '0.85rem' : '0.95rem' }}>PLM Consulting</span></li>
            <li className="mb-2"><span style={{ color: '#333333', fontSize: isMobile ? '0.85rem' : '0.95rem' }}>Teamcenter Services</span></li>
            <li className="mb-2"><span style={{ color: '#333333', fontSize: isMobile ? '0.85rem' : '0.95rem' }}>IT Infrastructure</span></li>
          </ul>
        </Col>

        <Col xs={6} lg={3}>
          <h5 className="fw-bold mb-3" style={{ color: '#0F2D62', fontSize: isMobile ? '0.95rem' : '1.1rem', textAlign: 'left' }}>Contact Info</h5>
          <ul className="list-unstyled" style={{ textAlign: 'left' }}>
            <li className="mb-2 d-flex align-items-center gap-2">
              <MapPin size={isMobile ? 14 : 16} color="#C61F2C" />
              <span style={{ color: '#333333', fontSize: isMobile ? '0.8rem' : '0.9rem' }}>Brampton, ON</span>
            </li>
            <li className="mb-2 d-flex align-items-center gap-2">
              <Phone size={isMobile ? 14 : 16} color="#C61F2C" />
              <span style={{ color: '#333333', fontSize: isMobile ? '0.8rem' : '0.9rem' }}>+1 (647) 977-4503</span>
            </li>
            <li className="mb-2 d-flex align-items-center gap-2">
              <Mail size={isMobile ? 14 : 16} color="#C61F2C" />
              <span style={{ color: '#333333', fontSize: isMobile ? '0.8rem' : '0.9rem' }}>contact@a1marketsense.ca</span>
            </li>
            <li className="mb-2 d-flex align-items-center gap-2">
              <Clock size={isMobile ? 14 : 16} color="#C61F2C" />
              <span style={{ color: '#333333', fontSize: isMobile ? '0.8rem' : '0.9rem' }}>9AM-6PM</span>
            </li>
          </ul>
        </Col>

        <Col xs={6} lg={3}>
          <h5 className="fw-bold mb-3" style={{ color: '#0F2D62', fontSize: isMobile ? '0.95rem' : '1.1rem', textAlign: 'left' }}>Get in Touch</h5>
          <div style={{ textAlign: 'left' }}>
            <button
              onClick={() => {
                // Use window.Calendly directly for popup
                if (window.Calendly) {
                  window.Calendly.initPopupWidget({
                    url: 'https://calendly.com/sahilumate32/30min',
                  });
                } else {
                  // Load script and then open
                  const script = document.createElement('script');
                  script.src = 'https://assets.calendly.com/assets/external/widget.js';
                  script.async = true;
                  script.onload = () => {
                    if (window.Calendly) {
                      window.Calendly.initPopupWidget({
                        url: 'https://calendly.com/sahilumate32/30min',
                      });
                    }
                  };
                  document.body.appendChild(script);
                }
              }}
              style={{ 
                fontSize: isMobile ? '0.85rem' : '1rem',
                padding: isMobile ? '10px 24px' : '12px 32px',
                width: isMobile ? '100%' : 'auto',
                display: 'inline-block',
                backgroundColor: '#C61F2C',
                border: '2px solid #C61F2C',
                borderRadius: '8px',
                color: '#FFFFFF',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                textDecoration: 'none',
                boxShadow: '0 4px 14px rgba(198, 31, 44, 0.35)',
                letterSpacing: '0.5px'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#0F2D62';
                e.target.style.borderColor = '#0F2D62';
                e.target.style.transform = 'translateY(-3px)';
                e.target.style.boxShadow = '0 8px 25px rgba(15, 45, 98, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#C61F2C';
                e.target.style.borderColor = '#C61F2C';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 14px rgba(198, 31, 44, 0.35)';
              }}
            >
              📅 Schedule
            </button>
          </div>
        </Col>
      </Row>

      <Row className="mt-5 pt-4">
        <Col className="text-center">
          <div style={{ borderTop: '1px solid #DDDDDD', paddingTop: '20px' }}>
            <p style={{ color: '#666666', fontSize: isMobile ? '0.7rem' : '0.8rem', marginBottom: '4px' }}>
              © {new Date().getFullYear()} A1 Marketsense. All Rights Reserved. 
              <span style={{ color: '#C61F2C', margin: '0 8px' }}>|</span> 
              <span style={{ color: '#666666' }}>Designed By Shiv technology</span>
            </p>
          </div>
        </Col>
      </Row>
    </Container>
  </footer>

        {/* Combined Support Popup */}
        <style>{`
          iframe[src*="tawk.to"] { display: none !important; }
          .tawk-min-container { display: none !important; }

          .support-float {
            position: fixed;
            bottom: 20px;
            right: 20px;
            width: 56px;
            height: 56px;
            background: #C61F2C;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            z-index: 9999;
            box-shadow: 0 4px 12px rgba(198, 31, 44, 0.25);
            transition: all 0.3s ease;
            animation: none;
            border: none;
            color: white;
          }

          .support-float:hover {
            transform: scale(1.05);
            background: #0F2D62;
            box-shadow: 0 6px 16px rgba(15, 45, 98, 0.3);
          }

          .support-float svg {
            width: 28px;
            height: 28px;
            fill: white;
          }

          .support-popup {
            position: fixed;
            bottom: 90px;
            right: 20px;
            width: 280px;
            background: #FFFFFF;
            border-radius: 12px;
            box-shadow: 0 8px 30px rgba(0,0,0,0.08);
            z-index: 9998;
            display: none;
            overflow: hidden;
            animation: slideIn 0.3s ease;
            border: 1px solid #E8E8E8;
          }

          .support-popup.show {
            display: block;
          }

          .popup-header {
            background: #0F2D62;
            color: white;
            padding: 16px 20px;
            text-align: center;
            position: relative;
          }

          .popup-header h3 {
            margin: 0;
            font-size: 16px;
            font-weight: 600;
            color: white;
          }

          .popup-header p {
            margin: 4px 0 0;
            font-size: 13px;
            opacity: 0.7;
            color: white;
          }

          .close-popup {
            position: absolute;
            top: 10px;
            right: 15px;
            color: white;
            font-size: 18px;
            cursor: pointer;
            opacity: 0.7;
            transition: opacity 0.3s;
            background: none;
            border: none;
          }

          .close-popup:hover {
            opacity: 1;
          }

          .popup-options {
            padding: 15px;
          }

          .support-option {
            display: flex;
            align-items: center;
            padding: 12px 15px;
            margin: 8px 0;
            border-radius: 8px;
            text-decoration: none;
            transition: all 0.3s ease;
            cursor: pointer;
            border: none;
            width: 100%;
            font-size: 14px;
          }

          .support-option:hover {
            transform: translateX(4px);
          }

          .option-icon {
            width: 36px;
            height: 36px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-right: 12px;
          }

          .option-icon svg {
            width: 20px;
            height: 20px;
            fill: white;
          }

          .option-text {
            flex: 1;
            font-weight: 500;
            text-align: left;
            color: #1B1B1B;
          }

          .whatsapp-option {
            background: #F0F9F0;
            border: 1px solid #E0F0E0;
          }

          .whatsapp-option .option-icon {
            background: #25D366;
          }

          .whatsapp-option:hover {
            border-color: #25D366;
            box-shadow: 0 4px 12px rgba(37, 211, 102, 0.12);
          }

          .tawkto-option {
            background: #F0F5FF;
            border: 1px solid #E0EAFF;
          }

          .tawkto-option .option-icon {
            background: #0F2D62;
          }

          .tawkto-option:hover {
            border-color: #0F2D62;
            box-shadow: 0 4px 12px rgba(15, 45, 98, 0.12);
          }

          .option-arrow {
            color: #C61F2C;
            font-size: 16px;
            transition: transform 0.3s ease;
          }

          .support-option:hover .option-arrow {
            transform: translateX(4px);
          }

          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @media (max-width: 768px) {
            .support-float {
              width: 48px;
              height: 48px;
              bottom: 15px;
              right: 15px;
            }

            .support-popup {
              bottom: 75px;
              right: 15px;
              width: 260px;
            }

            .support-float svg {
              width: 24px;
              height: 24px;
            }
          }
        `}</style>

        {(() => {
          const phoneNumber = "+16479844586";
          const message = encodeURIComponent("Hello A1 Marketsense, I have a question about your services");
          const whatsappUrl = `https://wa.me/${phoneNumber}?text=${message}`;

          const togglePopup = () => {
            document.getElementById('supportPopup').classList.toggle('show');
          };

          const openTawkTo = () => {
            if (typeof Tawk_API !== 'undefined') {
              Tawk_API.maximize();
            } else {
              window.location.href = whatsappUrl;
            }
            togglePopup();
          };

          return (
            <>
              <button onClick={togglePopup} className="support-float" aria-label="Support">
                <svg viewBox="0 0 24 24">
                  <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
                </svg>
              </button>

              <div className="support-popup" id="supportPopup">
                <div className="popup-header">
                  <button className="close-popup" onClick={togglePopup}>✕</button>
                  <h3>👋 How can we help?</h3>
                  <p>Choose your preferred way to chat</p>
                </div>
                
                <div className="popup-options">
                  <a 
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="support-option whatsapp-option"
                    onClick={togglePopup}
                  >
                    <div className="option-icon">
                      <svg viewBox="0 0 24 24">
                        <path d="M19.077 4.928C17.191 3.041 14.683 2 12.006 2c-5.35 0-9.703 4.342-9.708 9.693-.002 1.708.446 3.38 1.298 4.862L2 22l5.523-1.449c1.42.774 3.02 1.182 4.664 1.183h.004c5.35 0 9.704-4.343 9.709-9.694.002-2.591-1.005-5.027-2.893-6.912zM12.006 20.52h-.003c-1.457 0-2.886-.392-4.122-1.13l-.296-.176-3.276.86.876-3.194-.192-.306c-.809-1.29-1.236-2.78-1.235-4.3.004-4.443 3.618-8.055 8.066-8.055 2.153 0 4.177.84 5.698 2.363 1.521 1.523 2.358 3.55 2.355 5.703-.004 4.444-3.618 8.056-8.063 8.056z"/>
                      </svg>
                    </div>
                    <span className="option-text">Chat on WhatsApp</span>
                    <span className="option-arrow">→</span>
                  </a>

                  <button onClick={openTawkTo} className="support-option tawkto-option">
                    <div className="option-icon">
                      <svg viewBox="0 0 24 24">
                        <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
                      </svg>
                    </div>
                    <span className="option-text">Live Chat (Tawk.to)</span>
                    <span className="option-arrow">→</span>
                  </button>
                </div>
              </div>
            </>
          );
        })()}
      </div>
    </ErrorBoundary>
  );
}

export default App;